import * as React from 'react'
import * as PropTypes from 'prop-types'
import { omit } from 'lodash-es'
import { css, Rule } from 'glamor'

import { isKey, isElement } from '../../utils'
import { useTheme } from '../../theme'
import { UiVariant } from '../../types'

import getStyles from './styles'

const types = {
  unordered: 'ul',
  ordered: 'ol',
  definition: 'dl',
}

const sizes = ['xSmall', 'small', 'medium', 'large']

const cloneItem = (
  props: {
    item: React.ReactElement
    odd: boolean
  } & IListItemProps
) =>
  React.cloneElement(props.item, {
    element: props.listType !== 'definition' ? 'li' : props.odd ? 'dt' : 'dd',
    ...props,
  })

interface IListProps {
  children?: React.ReactNode
  delimit?: boolean
  displayBullets?: boolean
  flush?: boolean
  tight?: boolean
  size?: 'xSmall' | 'small' | 'medium' | 'large'
  style?: Rule
  type?: 'unordered' | 'ordered' | 'definition'
  variant?: UiVariant
}

export const List = ({
  children,
  type = 'unordered',
  size = 'small',
  delimit,
  displayBullets,
  style,
  flush,
  tight,
  variant,
}: IListProps) => {
  const styles = getStyles(useTheme())
  const childrenArr = React.Children.toArray(children)
  return React.createElement(
    types[type],
    {
      ...css(styles.list.base, tight && styles.list.tight, flush && styles.list.flush, style),
    },
    childrenArr.map((item, index) =>
      isElement(item)
        ? cloneItem({
            item,
            delimit,
            size,
            odd: (index + 1) % 2 !== 0,
            index,
            listType: type,
            variant,
            displayBullets,
            last: index === childrenArr.length - 1 || (type === 'definition' && index === childrenArr.length - 2),
            first: index === 0 || (type === 'definition' && index === 1),
          })
        : item
    )
  )
}

List.propTypes = {
  children: PropTypes.node,
  delimit: PropTypes.bool,
  displayBullets: PropTypes.bool,
  flush: PropTypes.bool,
  tight: PropTypes.bool,
  size: PropTypes.oneOf(sizes),
  style: PropTypes.object,
  type: PropTypes.oneOf(Object.keys(types)),
  variant: PropTypes.oneOf(['cta', 'primary', 'secondary', 'positive', 'negative', 'alert', 'brand']),
}

List.defaultProps = {
  size: 'small',
  type: 'unordered',
  flush: false,
  tight: false,
}

List.displayName = 'List'

List.VARIANTS = ['cta', 'primary', 'secondary', 'positive', 'negative', 'alert', 'brand']
List.TYPES = Object.keys(types)
List.SIZES = sizes

interface IListItemProps {
  children?: React.ReactNode
  delimit?: boolean
  element?: 'li' | 'dt' | 'dd'
  last?: boolean
  listType?: 'unordered' | 'ordered' | 'definition'
  onClick?: () => void
  size?: 'xSmall' | 'small' | 'medium' | 'large'
  style?: Rule
  displayBullets?: boolean
  index?: number
  first?: boolean
  variant?: UiVariant
}

export const ListItem = ({
  children,
  element = 'li',
  listType,
  delimit,
  size,
  index,
  last,
  first,
  style,
  onClick,
  displayBullets,
  variant,
  ...additional
}: IListItemProps) => {
  const styles = getStyles(useTheme())
  return React.createElement(
    element,
    {
      ...css(
        styles.item.base,
        styles.item.element[element],
        isKey(styles.item.sizes, size) && styles.item.sizes[size],
        delimit && styles.item.withDelimiter,
        first && styles.item.first,
        last && styles.item.last,
        onClick && styles.item.clickable,
        (listType === 'ordered' || listType === 'unordered') && { display: 'flex' },
        style
      ),
      onClick,
      ...(['li', 'dt', 'dd'].includes(element) ? omit(additional, ['odd', 'even', 'item']) : additional),
    },
    (listType === 'ordered' || listType === 'unordered') &&
      displayBullets &&
      React.createElement(
        'i',
        {
          ...css(
            styles.itemBullet.base,
            isKey(styles.itemBullet.variants, variant) && styles.itemBullet.variants[variant]
          ),
        },
        listType === 'ordered' ? (index || 0) + 1 : '∙'
      ),
    React.createElement('span', { ...css(styles.itemWrapper.base) }, children)
  )
}

ListItem.propTypes = {
  children: PropTypes.node,
  delimit: PropTypes.bool,
  element: PropTypes.oneOf(['li', 'dt', 'dd']),
  last: PropTypes.bool,
  listType: PropTypes.string,
  onClick: PropTypes.func,
  size: PropTypes.oneOf(sizes),
  style: PropTypes.object,
  displayBullets: PropTypes.bool,
}

ListItem.displayName = 'ListItem'
